import { useState } from 'react';
import { IonAlert, IonButton, IonChip, IonIcon, IonLabel, IonSpinner } from '@ionic/react';
import { bookmarkOutline, locateOutline, trashOutline } from 'ionicons/icons';
import { listPlaces, savePlace, deletePlace, touchPlace, placeToCoords } from '../lib/places';
import { getCurrentPosition } from '../lib/tracking';
import type { Coords, SavedPlace } from '../lib/types';

interface Props {
  value: Coords | null;
  onChange: (coords: Coords) => void;
  onError?: (message: string) => void;
}

/**
 * Schnellauswahl: gespeicherte Orte als Chips, dazu "Mein Standort" und
 * das Speichern des aktuell gewählten Punktes unter eigenem Namen.
 */
export function PlaceSelector({ value, onChange, onError }: Props) {
  const [places, setPlaces] = useState<SavedPlace[]>(() => listPlaces());
  const [locating, setLocating] = useState(false);
  const [naming, setNaming] = useState(false);
  const [pendingDelete, setPendingDelete] = useState<SavedPlace | null>(null);

  async function useCurrentPosition() {
    setLocating(true);
    try {
      const fix = await getCurrentPosition();
      onChange({ lat: fix.lat, lng: fix.lng, accuracy: fix.accuracy ?? undefined });
    } catch (error) {
      onError?.(error instanceof Error ? error.message : 'Standort nicht verfügbar.');
    } finally {
      setLocating(false);
    }
  }

  function choose(place: SavedPlace) {
    setPlaces(touchPlace(place.id));
    onChange(placeToCoords(place));
  }

  return (
    <>
      <div className="r2f-row" style={{ gap: 8, flexWrap: 'wrap' }}>
        <IonButton size="small" fill="solid" onClick={useCurrentPosition} disabled={locating}>
          {locating ? <IonSpinner name="dots" /> : <IonIcon slot="start" icon={locateOutline} />}
          Mein Standort
        </IonButton>
        <IonButton
          size="small"
          fill="outline"
          disabled={!value}
          onClick={() => setNaming(true)}
        >
          <IonIcon slot="start" icon={bookmarkOutline} />
          Ort speichern
        </IonButton>
      </div>

      {places.length > 0 && (
        <>
          <h3 className="r2f-setup__sectiontitle">Gespeicherte Orte</h3>
          <div className="r2f-chips">
          {places.map((place) => (
            <IonChip key={place.id} outline onClick={() => choose(place)}>
              <IonLabel>{place.name}</IonLabel>
              <IonIcon
                icon={trashOutline}
                aria-label={`${place.name} löschen`}
                onClick={(event) => {
                  event.stopPropagation();
                  setPendingDelete(place);
                }}
              />
            </IonChip>
          ))}
          </div>
        </>
      )}

      <IonAlert
        isOpen={naming}
        header="Ort speichern"
        message="Unter welchem Namen soll dieser Punkt in der Schnellauswahl stehen?"
        inputs={[{ name: 'name', type: 'text', placeholder: 'z. B. Zuhause', attributes: { maxlength: 40 } }]}
        buttons={[
          { text: 'Abbrechen', role: 'cancel' },
          {
            text: 'Speichern',
            handler: (data: { name?: string }) => {
              if (!value) return;
              savePlace(data.name ?? '', value);
              setPlaces(listPlaces());
            },
          },
        ]}
        onDidDismiss={() => setNaming(false)}
      />

      <IonAlert
        isOpen={pendingDelete !== null}
        header="Ort löschen?"
        message={pendingDelete ? `"${pendingDelete.name}" aus der Schnellauswahl entfernen.` : ''}
        buttons={[
          { text: 'Behalten', role: 'cancel' },
          {
            text: 'Löschen',
            role: 'destructive',
            handler: () => {
              if (pendingDelete) setPlaces(deletePlace(pendingDelete.id));
            },
          },
        ]}
        onDidDismiss={() => setPendingDelete(null)}
      />
    </>
  );
}
